import React from 'react';
import { PayloadComparisonViewer } from '../../../payload-comparison-viewer';
import * as S from '../claim-timeline.styles';
import { ICodingDetailProps } from './coding-detail.types';

/**
 * CodingDetail Component
 * 
 * Displays detailed information about a coding record including:
 * - Coding ID, Vendor, Status
 * - Notes (handles double-encoded JSON)
 * - Before/After/Diff comparison
 * 
 * Used by both Provider and Support apps in their timeline views.
 */
export const CodingDetail: React.FunctionComponent<ICodingDetailProps> = (
    props: ICodingDetailProps,
): React.ReactElement => {
    const { coding } = props;
    const beforePayload = coding.before ? JSON.stringify(coding.before, null, 4) : '';
    const afterPayload = coding.after ? JSON.stringify(coding.after, null, 4) : '';
    const showDiff = !!coding.before && !!coding.after;

    let codingNotes = coding.notes;

    // Handle double encoded coding notes
    if (codingNotes) {
        try {
            codingNotes = JSON.parse(codingNotes);
        } catch (e) {
            // Keep original string if not JSON
        }
    }

    return (
        <S.ExpandWrap key={coding.id}>
            <S.ExpandCol>
                <S.ExpandTable expand={true}>
                    <S.ExpandTBody>
                        <S.ExpandTR>
                            <S.ExpandTH>Coding ID</S.ExpandTH>
                            <S.ExpandTD>{coding.id}</S.ExpandTD>
                        </S.ExpandTR>
                        <S.ExpandTR>
                            <S.ExpandTH>Vendor</S.ExpandTH>
                            <S.ExpandTD>{coding.vendor}</S.ExpandTD>
                        </S.ExpandTR>
                        <S.ExpandTR>
                            <S.ExpandTH>Status</S.ExpandTH>
                            <S.ExpandTD>{coding.status}</S.ExpandTD>
                        </S.ExpandTR>
                        {codingNotes && (
                            <S.ExpandTR>
                                <S.ExpandTH>Notes</S.ExpandTH>
                                <S.ExpandTD>
                                    <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '12px' }}>
                                        {typeof codingNotes === 'string'
                                            ? codingNotes
                                            : JSON.stringify(codingNotes, null, 2)}
                                    </pre>
                                </S.ExpandTD>
                            </S.ExpandTR>
                        )}
                        <S.ExpandTR>
                            <S.ExpandTD colSpan={2}>
                                <PayloadComparisonViewer
                                    currentX12={afterPayload}
                                    previousX12={beforePayload}
                                    showDiff={showDiff}
                                    previousLabel="Before"
                                    currentLabel="After"
                                />
                            </S.ExpandTD>
                        </S.ExpandTR>
                    </S.ExpandTBody>
                </S.ExpandTable>
            </S.ExpandCol>
        </S.ExpandWrap>
    );
};
